import { Controller } from '@hotwired/stimulus';
import { patchTurboStream } from '../utils/turbo_stream_patch.js';

/** Durée de vie du toast après un solde (ms). */
export const TOAST_MS = 5000;

/**
 * Remark Undo Toast Controller
 *
 * Revue mobile (retouché le 2026-10-10) — le toast « OBS-41 soldée · Annuler » qui
 * suit le glissement « Solder » d'une carte §7 (`remark_swipe_controller.js`).
 * Monté UNE fois, hors de la §7 (`meeting_reports/remarks/_undo_toast.html.erb`) :
 * la section est remplacée en bloc par les streams, le contrôleur de la carte meurt
 * avec elle, celui-ci survit.
 *
 * RÈGLE D'ANNULATION : « Annuler » n'annule QUE la DERNIÈRE remarque soldée. Un
 * nouveau `remark-swipe:committed` REMPLACE le contenu et relance le minuteur ; la
 * précédente reste soldée (elle se rouvre depuis sa fiche). « Annuler » envoie
 * `PATCH undoUrl` (`remarques/:id?depuis=liste`) avec le seul
 * `observation[remark_status_id]` d'avant le geste, puis le toast se ferme. Une
 * remarque orpheline (pas de statut d'avant) n'offre pas « Annuler ».
 *
 * Targets:
 * - bar: la barre visible (propriété `hidden`)
 * - message: « OBS-41 soldée »
 * - undo: l'enveloppe du bouton « Annuler » (masquée sans statut d'avant)
 * - undoButton: le bouton lui-même (désactivé pendant la requête)
 * - errorTemplate: le toast d'échec, rendu serveur, recopié dans `#flash-container`
 *
 * Values:
 * - duration: durée de vie du toast (défaut 5000 ms)
 *
 * Actions:
 * - show: `remark-swipe:committed@window` — `{ ref, undoUrl, previousStatusId }`
 * - fail: `remark-swipe:failed@window` — `{ rendered }`
 * - undo: le bouton « Annuler »
 *
 * @example
 * <div data-controller="remark-undo-toast" role="status" aria-live="polite"
 *      data-action="remark-swipe:committed@window->remark-undo-toast#show
 *                   remark-swipe:failed@window->remark-undo-toast#fail">
 *   <div data-remark-undo-toast-target="bar" hidden>
 *     <span data-remark-undo-toast-target="message"></span>
 *     <span data-remark-undo-toast-target="undo">
 *       <button data-remark-undo-toast-target="undoButton"
 *               data-action="remark-undo-toast#undo">Annuler</button>
 *     </span>
 *   </div>
 *   <template data-remark-undo-toast-target="errorTemplate">…</template>
 * </div>
 */
export default class extends Controller {
  static targets = ['bar', 'message', 'undo', 'undoButton', 'errorTemplate'];
  static values = { duration: { type: Number, default: TOAST_MS } };

  connect() {
    this.current = null;
    this.timer = null;
  }

  disconnect() {
    this.#clearTimer();
    this.current = null;
  }

  /** @param {CustomEvent} event `remark-swipe:committed` */
  show(event) {
    const { ref, undoUrl, previousStatusId } = event.detail || {};
    this.current = { ref, undoUrl, previousStatusId: previousStatusId || '' };

    // La barre d'abord, le texte ensuite : la région live annonce un texte qui
    // apparaît dans un nœud VISIBLE.
    this.barTarget.hidden = false;
    this.messageTarget.textContent = `${ref} soldée`;
    if (this.hasUndoTarget) this.undoTarget.hidden = this.current.previousStatusId === '';
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = false;
    this.#restartTimer();
  }

  /** @param {CustomEvent} event `remark-swipe:failed` */
  fail(event) {
    if (event.detail?.rendered) return;
    this.#showErrorToast();
  }

  async undo(event) {
    event?.preventDefault();
    const entry = this.current;
    if (!entry || entry.previousStatusId === '' || entry.pending) return;

    entry.pending = true;
    this.#clearTimer();
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = true;

    const body = new FormData();
    body.append('observation[remark_status_id]', entry.previousStatusId);
    const result = await patchTurboStream(entry.undoUrl, body);
    if (!result.rendered) this.#showErrorToast();

    // Un solde arrivé pendant la requête a remplacé le toast : il reste affiché.
    if (this.current === entry) this.hide();
  }

  hide() {
    this.#clearTimer();
    this.current = null;
    this.barTarget.hidden = true;
    this.messageTarget.textContent = '';
  }

  #restartTimer() {
    this.#clearTimer();
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.hide();
    }, this.durationValue);
  }

  #clearTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }

  #showErrorToast() {
    const container = document.getElementById('flash-container');
    if (!container || !this.hasErrorTemplateTarget) return;

    container.prepend(this.errorTemplateTarget.content.cloneNode(true));
  }
}
